import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CustomFieldDefinition, CustomFieldTyp } from '../hooks/useCustomFields';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Props {
  authToken: string;
}

const ERLAUBTE_TABELLEN = ['artikel', 'partner', 'auftrag', 'bestellung', 'lagerbestand', 'qualitaetspruefung'];
const TYPEN: CustomFieldTyp[] = ['TEXT', 'NUMBER', 'DATE', 'BOOLEAN', 'SELECT', 'TEXTAREA'];

const CustomFieldsManager: React.FC<Props> = ({ authToken }) => {
  const [definitions, setDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterTabelle, setFilterTabelle] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);
  const [newOptionInputs, setNewOptionInputs] = useState<Record<number, string>>({});
  const [conflict, setConflict] = useState<{ feld: CustomFieldDefinition; wert: string; anzahl: number; ersatzWert: string } | null>(null);

  const [newField, setNewField] = useState({
    tabelle: 'artikel',
    schluessel: '',
    label: '',
    typ: 'TEXT' as CustomFieldTyp,
    optionen: '',
    pflicht: false,
    default_wert: '',
    beschreibung: '',
  });

  const headers = { Authorization: `Bearer ${authToken}` };

  const load = async () => {
    setLoading(true);
    try {
      const params: any = { nur_aktive: false };
      if (filterTabelle) params.tabelle = filterTabelle;
      const res = await axios.get(`${API_URL}/api/custom-fields`, { headers, params });
      setDefinitions(res.data);
    } catch (e: any) {
      setFeedback({ ok: false, text: e.response?.data?.detail || 'Laden fehlgeschlagen' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [authToken, filterTabelle]); // eslint-disable-line

  const handleCreate = async () => {
    if (!newField.schluessel || !newField.label) {
      setFeedback({ ok: false, text: 'Schlüssel und Label sind Pflicht' });
      return;
    }
    try {
      const payload: any = {
        tabelle: newField.tabelle,
        schluessel: newField.schluessel,
        label: newField.label,
        typ: newField.typ,
        pflicht: newField.pflicht,
      };
      if (newField.typ === 'SELECT') {
        payload.optionen = newField.optionen.split(',').map(s => s.trim()).filter(Boolean);
      }
      if (newField.default_wert) payload.default_wert = newField.default_wert;
      if (newField.beschreibung) payload.beschreibung = newField.beschreibung;

      await axios.post(`${API_URL}/api/custom-fields`, payload, { headers });
      setFeedback({ ok: true, text: `Feld '${newField.label}' angelegt` });
      setShowForm(false);
      setNewField({ tabelle: 'artikel', schluessel: '', label: '', typ: 'TEXT', optionen: '', pflicht: false, default_wert: '', beschreibung: '' });
      load();
    } catch (e: any) {
      setFeedback({ ok: false, text: e.response?.data?.detail || 'Anlegen fehlgeschlagen' });
    }
  };

  const handleAddOption = async (def: CustomFieldDefinition) => {
    const wert = (newOptionInputs[def.feld_id] || '').trim();
    if (!wert) return;
    if ((def.optionen || []).some(o => o.toLowerCase() === wert.toLowerCase())) {
      setFeedback({ ok: false, text: `Option '${wert}' existiert bereits` });
      return;
    }
    try {
      await axios.put(`${API_URL}/api/custom-fields/${def.feld_id}`,
        { optionen: [...(def.optionen || []), wert] }, { headers });
      setNewOptionInputs({ ...newOptionInputs, [def.feld_id]: '' });
      setFeedback({ ok: true, text: `Option '${wert}' hinzugefügt` });
      load();
    } catch (e: any) {
      setFeedback({ ok: false, text: e.response?.data?.detail || 'Fehler beim Hinzufügen' });
    }
  };

  const handleRemoveOption = async (def: CustomFieldDefinition, wert: string) => {
    const neueOptionen = (def.optionen || []).filter(o => o !== wert);
    try {
      await axios.put(`${API_URL}/api/custom-fields/${def.feld_id}`, { optionen: neueOptionen }, { headers });
      setFeedback({ ok: true, text: `Option '${wert}' entfernt` });
      load();
    } catch (e: any) {
      if (e.response?.status === 409) {
        const betroffene = e.response.data?.detail?.betroffene || [];
        const treffer = betroffene.find((b: any) => b.wert === wert);
        setConflict({ feld: def, wert, anzahl: treffer?.anzahl || 0, ersatzWert: '' });
      } else {
        setFeedback({ ok: false, text: e.response?.data?.detail || 'Fehler beim Entfernen' });
      }
    }
  };

  const handleResolveConflict = async () => {
    if (!conflict || !conflict.ersatzWert) return;
    const neueOptionen = (conflict.feld.optionen || []).filter(o => o !== conflict.wert);
    try {
      await axios.put(`${API_URL}/api/custom-fields/${conflict.feld.feld_id}`, {
        optionen: neueOptionen,
        ersatz_werte: { [conflict.wert]: conflict.ersatzWert },
      }, { headers });
      setFeedback({ ok: true, text: `Option '${conflict.wert}' entfernt, Datensätze auf '${conflict.ersatzWert}' migriert` });
      setConflict(null);
      load();
    } catch (e: any) {
      setFeedback({ ok: false, text: e.response?.data?.detail || 'Migration fehlgeschlagen' });
    }
  };

  const handleToggleAktiv = async (def: CustomFieldDefinition) => {
    try {
      if (def.aktiv) {
        await axios.delete(`${API_URL}/api/custom-fields/${def.feld_id}`, { headers });
      } else {
        await axios.put(`${API_URL}/api/custom-fields/${def.feld_id}`, { aktiv: true }, { headers });
      }
      load();
    } catch (e: any) {
      setFeedback({ ok: false, text: e.response?.data?.detail || 'Fehler' });
    }
  };

  return (
    <div style={{ background: 'var(--bone)', borderRadius: 0, padding: 32, boxShadow: 'none' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22 }}>Custom-Felder verwalten</h2>
        <button
          onClick={() => setShowForm(true)}
          style={{ padding: '8px 16px', background: 'var(--green-500)', color: 'var(--bone)', border: 'none', borderRadius: 0, cursor: 'pointer', fontWeight: 600 }}
        >
          + Neues Feld
        </button>
      </div>

      <p style={{ color: 'var(--ink-2)', fontSize: 14, marginBottom: 20 }}>
        Erweitere Tabellen um eigene Felder. Auch über Chat möglich:
        <em style={{ color: 'var(--green-500)' }}> "Füge Feld 'Mindesthaltbarkeit' (Datum) zum Lagerbestand hinzu"</em>
      </p>

      <div style={{ marginBottom: 16 }}>
        <select
          value={filterTabelle}
          onChange={e => setFilterTabelle(e.target.value)}
          style={{ padding: '6px 12px', borderRadius: 0, border: '1px solid var(--bone-3)' }}
        >
          <option value="">Alle Tabellen</option>
          {ERLAUBTE_TABELLEN.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {feedback && (
        <div style={{
          padding: '8px 16px', marginBottom: 16, borderRadius: 0,
          background: feedback.ok ? 'var(--bone-2)' : 'var(--bone-2)',
          color: feedback.ok ? 'var(--green-500)' : 'var(--red)', fontSize: 13,
        }}>
          {feedback.ok ? '✓' : '✗'} {feedback.text}
        </div>
      )}

      {/* Form für neues Feld */}
      {showForm && (
        <div style={{ background: 'var(--bone-2)', border: '1px solid var(--bone-2)', borderRadius: 0, padding: 20, marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, fontSize: 16 }}>Neues Custom-Feld anlegen</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Tabelle</label>
              <select value={newField.tabelle} onChange={e => setNewField({ ...newField, tabelle: e.target.value })}
                style={{ width: '100%', padding: 6, borderRadius: 0, border: '1px solid var(--bone-3)' }}>
                {ERLAUBTE_TABELLEN.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Typ</label>
              <select value={newField.typ} onChange={e => setNewField({ ...newField, typ: e.target.value as CustomFieldTyp })}
                style={{ width: '100%', padding: 6, borderRadius: 0, border: '1px solid var(--bone-3)' }}>
                {TYPEN.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Schlüssel (snake_case)</label>
              <input type="text" value={newField.schluessel}
                onChange={e => setNewField({ ...newField, schluessel: e.target.value })}
                placeholder="z.B. mhd, farbe, charge_typ"
                style={{ width: '100%', padding: 6, borderRadius: 0, border: '1px solid var(--bone-3)' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Label (Anzeige)</label>
              <input type="text" value={newField.label}
                onChange={e => setNewField({ ...newField, label: e.target.value })}
                placeholder="z.B. Mindesthaltbarkeit"
                style={{ width: '100%', padding: 6, borderRadius: 0, border: '1px solid var(--bone-3)' }} />
            </div>
            {newField.typ === 'SELECT' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 12, fontWeight: 600 }}>Optionen (Komma-getrennt)</label>
                <input type="text" value={newField.optionen}
                  onChange={e => setNewField({ ...newField, optionen: e.target.value })}
                  placeholder="z.B. Rot, Gelb, Grün"
                  style={{ width: '100%', padding: 6, borderRadius: 0, border: '1px solid var(--bone-3)' }} />
              </div>
            )}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Beschreibung (optional)</label>
              <input type="text" value={newField.beschreibung}
                onChange={e => setNewField({ ...newField, beschreibung: e.target.value })}
                style={{ width: '100%', padding: 6, borderRadius: 0, border: '1px solid var(--bone-3)' }} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={newField.pflicht}
                onChange={e => setNewField({ ...newField, pflicht: e.target.checked })} />
              Pflichtfeld
            </label>
            {newField.pflicht && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 600 }}>Default-Wert (Pflicht bei Pflichtfeld)</label>
                <input type="text" value={newField.default_wert}
                  onChange={e => setNewField({ ...newField, default_wert: e.target.value })}
                  style={{ width: '100%', padding: 6, borderRadius: 0, border: '1px solid var(--bone-3)' }} />
              </div>
            )}
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button onClick={handleCreate}
              style={{ padding: '8px 16px', background: 'var(--green-500)', color: 'var(--bone)', border: 'none', borderRadius: 0, cursor: 'pointer', fontWeight: 600 }}>
              Anlegen
            </button>
            <button onClick={() => setShowForm(false)}
              style={{ padding: '8px 16px', background: 'var(--bone)', color: 'var(--ink-2)', border: '1px solid var(--bone-3)', borderRadius: 0, cursor: 'pointer' }}>
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--ink-2)', padding: 40 }}>Lädt...</div>
      ) : definitions.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--ink-2)', padding: 40 }}>Noch keine Custom-Felder definiert.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bone-2)', textTransform: 'uppercase', fontSize: 12, letterSpacing: 0.5 }}>
              <th style={{ padding: 12, textAlign: 'left' }}>Tabelle</th>
              <th style={{ padding: 12, textAlign: 'left' }}>Schlüssel</th>
              <th style={{ padding: 12, textAlign: 'left' }}>Label</th>
              <th style={{ padding: 12, textAlign: 'left' }}>Typ</th>
              <th style={{ padding: 12, textAlign: 'left' }}>Pflicht</th>
              <th style={{ padding: 12, textAlign: 'left' }}>Status</th>
              <th style={{ padding: 12 }}></th>
            </tr>
          </thead>
          <tbody>
            {definitions.map(d => (
              <React.Fragment key={d.feld_id}>
                <tr style={{ borderBottom: d.typ === 'SELECT' ? 'none' : '1px solid var(--bone-3)', opacity: d.aktiv ? 1 : 0.5 }}>
                  <td style={{ padding: 12 }}><code style={{ background: 'var(--bone-2)', padding: '2px 6px', borderRadius: 0 }}>{d.tabelle}</code></td>
                  <td style={{ padding: 12, fontFamily: 'monospace', fontSize: 13 }}>{d.schluessel}</td>
                  <td style={{ padding: 12 }}>{d.label}</td>
                  <td style={{ padding: 12 }}>
                    <span style={{ padding: '2px 10px', borderRadius: 0, fontSize: 12, background: 'var(--bone-2)', color: 'var(--green-900)' }}>
                      {d.typ}
                    </span>
                  </td>
                  <td style={{ padding: 12 }}>{d.pflicht ? '✓' : '–'}</td>
                  <td style={{ padding: 12 }}>
                    <span style={{
                      padding: '2px 10px', borderRadius: 0, fontSize: 12,
                      background: d.aktiv ? 'var(--bone-2)' : 'var(--bone-2)',
                      color: d.aktiv ? 'var(--green-500)' : 'var(--red)',
                    }}>
                      {d.aktiv ? 'Aktiv' : 'Deaktiviert'}
                    </span>
                  </td>
                  <td style={{ padding: 12, textAlign: 'right' }}>
                    <button onClick={() => handleToggleAktiv(d)}
                      style={{ padding: '4px 10px', background: 'none', border: '1px solid var(--bone-3)', borderRadius: 0, cursor: 'pointer', fontSize: 12 }}>
                      {d.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                    </button>
                  </td>
                </tr>
                {d.typ === 'SELECT' && (
                  <tr style={{ borderBottom: '1px solid var(--bone-3)', opacity: d.aktiv ? 1 : 0.5 }}>
                    <td></td>
                    <td colSpan={6} style={{ padding: '0 12px 14px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                        {(d.optionen || []).map(opt => (
                          <span key={opt} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '3px 4px 3px 10px', border: '1px solid var(--bone-3)',
                            borderRadius: 0, fontSize: 12,
                          }}>
                            {opt}
                            {d.aktiv && (
                              <button onClick={() => handleRemoveOption(d, opt)} title={`'${opt}' entfernen`}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-2)', fontSize: 13, lineHeight: 1, padding: '0 4px' }}>
                                ×
                              </button>
                            )}
                          </span>
                        ))}
                        {d.aktiv && (
                          <span style={{ display: 'inline-flex', gap: 4 }}>
                            <input
                              type="text"
                              value={newOptionInputs[d.feld_id] || ''}
                              onChange={e => setNewOptionInputs({ ...newOptionInputs, [d.feld_id]: e.target.value })}
                              onKeyDown={e => { if (e.key === 'Enter') handleAddOption(d); }}
                              placeholder="Neue Option"
                              style={{ padding: '4px 8px', fontSize: 12, borderRadius: 0, border: '1px solid var(--bone-3)', width: 140 }}
                            />
                            <button onClick={() => handleAddOption(d)}
                              style={{ padding: '4px 10px', background: 'none', border: '1px solid var(--bone-3)', borderRadius: 0, cursor: 'pointer', fontSize: 12 }}>
                              Hinzufügen
                            </button>
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {conflict && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
        }}>
          <div style={{ background: 'var(--bone)', padding: 24, width: 420, border: '1px solid var(--bone-3)' }}>
            <h3 style={{ marginTop: 0, fontSize: 16 }}>Option wird verwendet</h3>
            <p style={{ fontSize: 14, color: 'var(--ink-2)' }}>
              Wert '<strong>{conflict.wert}</strong>' wird von <strong>{conflict.anzahl}</strong> Datensätzen verwendet.
              Wähle einen Ersatzwert, um sie zu migrieren, bevor die Option entfernt wird.
            </p>
            <select
              value={conflict.ersatzWert}
              onChange={e => setConflict({ ...conflict, ersatzWert: e.target.value })}
              style={{ width: '100%', padding: 8, borderRadius: 0, border: '1px solid var(--bone-3)', marginBottom: 16 }}
            >
              <option value="">— Ersatzwert wählen —</option>
              {(conflict.feld.optionen || []).filter(o => o !== conflict.wert).map(o => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setConflict(null)}
                style={{ padding: '8px 16px', background: 'none', border: '1px solid var(--bone-3)', borderRadius: 0, cursor: 'pointer' }}>
                Abbrechen
              </button>
              <button onClick={handleResolveConflict} disabled={!conflict.ersatzWert}
                style={{ padding: '8px 16px', background: 'var(--green-500)', color: 'var(--bone)', border: 'none', borderRadius: 0, cursor: conflict.ersatzWert ? 'pointer' : 'default', opacity: conflict.ersatzWert ? 1 : 0.5, fontWeight: 600 }}>
                Migrieren &amp; entfernen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFieldsManager;
